import { type } from 'os';
import type { FormSubmissionData, IResourceNode, ResourceSchemaData } from 'packages/workspace/services/models/ChannelResource';
import type { ResponseFormat } from 'packages/workspace/services/models/global-models';
import type { IResourceFormSubmission } from 'packages/workspace/services/models/resource-form-submission';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { BehaviorSubject } from 'rxjs';
import useObservable from '../../helpers/hooks/useObservable';
import { Http } from '../../prelude/http';
import type { HttpModel } from '../../prelude/http/http';
import { TaskRouterConfig } from 'packages/workspace/services/config';
import { uiStore } from 'packages/workspace/services/store/ui-store';

export interface ITaskResourceIncrementCount {
  app_id: string;
  task_sid: string;
  document_id: string;
  name: string;
  send_as_button?: boolean;
  helper_text?: string;
}

export interface IFormSubmissionData {
  form_id: string;
  task_sid: string;
}

let lastChannelResourcesValidData: { allTabs: any[]; tabContents: any[] };
let lastDashboardChannelResourcesValidData: { allTabs: any[]; tabContents: any[] };
/**
 * Used to fetch Resources Per channel in Omnichannel.
 * @param channel_id
 * @param operator_profile_id
 */
const fetchChannelResources = async (
  channel_id: string,
  operator_profile_id: string,
  type: string,
  search_content: string,
  app_id: string,
  operator_sid: string,
  task_sid: string,
  search_preference: string
) =>
  Http.Request<ResponseFormat<IResourceNode[]>>(
    'GET',
    `/hotlines/api/v1/channel-resources.get?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&tab=${type}&app_id=${app_id}&operator_sid=${operator_sid}&task_sid=${task_sid}&search_content=${search_content}&search_preference=${search_preference}`
  )
    .then((res) => res.data)
    .then((res) => {
      if (!res || res.length === 0) {
        uiStore.setErrorMessage('No Resources Found.');
        throw new Error('No Resources Found.');
      }
      const allTabs = [];
      const tabContents = [];
      res.forEach((eachTab) => {
        const allBuckets = [];
        if (eachTab.elements && Object.keys(eachTab.elements).length > 0) {
          Object.values(eachTab.elements).forEach((val) => allBuckets.push(val));
        }
        if (allBuckets.length > 0) {
          allTabs.push({
            id: eachTab.id,
            displayName: eachTab.displayName,
            alias: eachTab.alias,
          });

          tabContents.push({ id: eachTab.id, contents: allBuckets });
        }
        if (tabContents.length === 0 || allTabs.length === 0) {
          uiStore.setErrorMessage('No Resources Found.');
          throw new Error('No Resources Found.');
        }
      });
      lastChannelResourcesValidData = {
        allTabs,
        tabContents,
      };
      return lastChannelResourcesValidData;
    })
    .catch((err) => {
      if (lastChannelResourcesValidData) {
        return lastChannelResourcesValidData;
      }
      throw err;
    });

const fetchDashboardChannelResources = async (channel_id: string, operator_profile_id: string, app_id: string, type: string, search_content: string, search_preference: string) =>
  Http.Request<ResponseFormat<IResourceNode[]>>(
    'GET',
    `/hotlines/api/v1/dashboard-channel-resources.get?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&app_id=${app_id}&tab=${type}&search_content=${search_content}&search_preference=${search_preference}`
  )
    .then((res) => res.data)
    .then((res) => {
      if (!res || res.length === 0) {
        uiStore.setErrorMessage('No Resources Found.');
        throw new Error('No Resources Found.');
      }
      const allTabs = [];
      const tabContents = [];
      res.forEach((eachTab) => {
        const allBuckets = [];
        if (eachTab.elements && Object.keys(eachTab.elements).length > 0) {
          Object.values(eachTab.elements).forEach((val) => allBuckets.push(val));
        }
        if (allBuckets.length > 0) {
          allTabs.push({
            id: eachTab.id,
            displayName: eachTab.displayName,
            alias: eachTab.alias,
          });
          tabContents.push({ id: eachTab.id, contents: allBuckets });
        }
        if (tabContents.length === 0 || allTabs.length === 0) {
          uiStore.setErrorMessage('No Resources Found.');
          throw new Error('No Resources Found.');
        }
      });
      lastDashboardChannelResourcesValidData = {
        allTabs,
        tabContents,
      };
      return lastDashboardChannelResourcesValidData;
    })
    .catch((err) => {
      if (lastDashboardChannelResourcesValidData) {
        return lastDashboardChannelResourcesValidData;
      }
      throw err;
    });

/**
 * Used to get Channel Resource Catalog Per Tags
 * @param task_sid
 */
export const useChannelResources = (
  channel_id: string,
  operator_profile_id: string,
  type: string,
  search_content: string,
  app_id: string,
  operator_sid: string,
  task_sid: string,
  search_preference: string
): UseQueryResult<{ allTabs; tabContents }> => {
  return useQuery(
    ['channel_resources', type, channel_id, operator_profile_id, search_content, task_sid, search_preference],
    () => fetchChannelResources(channel_id, operator_profile_id, type, search_content, app_id, operator_sid, task_sid, search_preference),
    {
      enabled:
        search_preference !== '' &&
        search_preference !== undefined &&
        search_preference !== 'keyword' &&
        operator_profile_id !== '' &&
        operator_profile_id !== undefined &&
        app_id !== undefined &&
        app_id !== '' &&
        operator_sid !== undefined &&
        task_sid !== undefined,
    }
  );
};

export const useDashboardChannelResources = (
  channel_id: string,
  operator_profile_id: string,
  app_id: string,
  type: string,
  search_content: string,
  search_preference: string
): UseQueryResult<{ allTabs; tabContents }> => {
  return useQuery(
    ['dashboard_channel_resources', type, channel_id, operator_profile_id, search_content, search_preference],
    () => fetchDashboardChannelResources(channel_id, operator_profile_id, app_id, type, search_content, search_preference),
    {
      enabled:
        search_preference !== '' &&
        search_preference !== undefined &&
        search_preference !== 'keyword' &&
        operator_profile_id !== '' &&
        operator_profile_id !== undefined &&
        app_id !== undefined &&
        app_id !== '' &&
        channel_id !== '',
    }
  );
};

export const useDashboardSearchByKeyword = (
  channel_id: string,
  operator_profile_id: string,
  tab: string,
  search_content: string,
  app_id: string,
  search_preference: string,
  start: number,
  size: number
): UseQueryResult<unknown> => {
  return useQuery(
    ['dashboard-resources-keyword', channel_id, operator_profile_id, tab, search_content, app_id, search_preference, start, size],
    () =>
      Http.Request<ResponseFormat<unknown>>(
        'GET',
        `/hotlines/api/v1/dashboard-channel-resources.search?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&tab=${tab}&search_content=${search_content}&app_id=${app_id}&search_preference=${search_preference}&start=${start}&size=${size}`
      ).then((res) => res.data),
    {
      enabled: search_preference === 'keyword' && search_content !== '' && tab !== undefined,
    }
  );
};

export const fetchReferencesContent = (id: string) => Http.Request<ResponseFormat<IResourceNode>>('GET', `/resources/api/v1/documents.content?document_id=${id}`).then((res) => res.data);

export function useReferencesContent(id: string, enabled = true): UseQueryResult<IResourceNode> {
  return useQuery(['document_content', id], () => fetchReferencesContent(id), {
    enabled: id !== '' && enabled,
  });
}

export function useAssessmentsContent(id: string): UseQueryResult<ResourceSchemaData> {
  return useQuery(['document_content', id], () => Http.Request<ResponseFormat<ResourceSchemaData>>('GET', `/resources/api/v1/document-forms.content?document_id=${id}`).then((res) => res.data), {
    enabled: id !== '',
  });
}

export const useTaskResourceIncrementCount = (): UseMutationResult<string, unknown, ITaskResourceIncrementCount, unknown> => {
  return useMutation(async ({ app_id, task_sid, document_id, name, send_as_button = false, helper_text = '' }) =>
    Http.Request<ResponseFormat<string>>(
      'POST',
      `/hotlines/api/v1/omnichannel/tasks.resource?app_id=${app_id}&task_sid=${task_sid}&id=${document_id}&name=${encodeURIComponent(
        name
      )}&send_as_button=${send_as_button}&helper_text=${encodeURIComponent(helper_text)}`
    ).then((res) => res.data)
  );
};

export function useFormSubmissionData(form_id: string, task_sid: string, app_id: string, operator_sid: string, channel_id: string): UseQueryResult<FormSubmissionData> {
  return useQuery(
    ['document_content', form_id, task_sid],
    () =>
      Http.Request<ResponseFormat<FormSubmissionData>>(
        'GET',
        `/hotlines/api/v1/channel-form-submissions.get?form_id=${form_id}&task_sid=${task_sid}&app_id=${app_id}&operator_sid=${operator_sid}&channel_id=${channel_id}`
      ).then((res) => res.data),
    {
      enabled: form_id !== '' && task_sid !== '',
    }
  );
}

export function useFormSubmission() {
  return useMutation((updateData: HttpModel.IRequestPayload) => Http.Request<IResourceFormSubmission>('POST', '/resources/api/v1/form-submissions.create', updateData));
}

//Selected Resource Subject
const selectedResource$ = new BehaviorSubject({ id: '', displayName: '' });
export const ResourcesHelper = {
  setSelectedResource: (selectedResource: string, displayName: string): void => selectedResource$.next({ id: selectedResource, displayName }),
  clearSelection: (): void => selectedResource$.next({ id: '', displayName: '' }),
};

export default function useSelectedResource() {
  return useObservable<{ id: string; displayName: string }>(selectedResource$, selectedResource$.value);
}

//Selected form Subject
const selectedForm$ = new BehaviorSubject({ id: '', displayName: '' });
export const FormsHelper = {
  setSelectedResource: (selectedForm: string, displayName: string): void => selectedForm$.next({ id: selectedForm, displayName }),
  clearSelection: (): void => selectedForm$.next({ id: '', displayName: '' }),
};

export function useSelectedForm() {
  return useObservable<{ id: string; displayName: string }>(selectedForm$, selectedForm$.value);
}

//selected script subject
const selectedScript$ = new BehaviorSubject({ id: '', displayName: '' });
export const ScriptsHelper = {
  setSelectedScript: (selectedScript: string, displayName: string): void => selectedScript$.next({ id: selectedScript, displayName }),
  clearSelection: (): void => selectedScript$.next({ id: '', displayName: '' }),
};

export function useSelectedScript() {
  return useObservable<{ id: string; displayName: string }>(selectedScript$, selectedScript$.value);
}
/**
 *! API For getting list of forms rather than tree structure.
  #### Date : 18th/Jan/2022
 * @param app_id
 * @param channel_id
 * @param operator_sid
 * @param task_sid
 * @param operator_profile_id
 * @param tab
 */

interface IFormResponse {
  id: string;
  name: string;
}

export const useChannelForms = (app_id: string, channel_id: string, operator_sid: string, task_sid: string, operator_profile_id: string, tab: string): UseQueryResult<IFormResponse[]> => {
  return useQuery(
    ['channel_forms', channel_id, operator_profile_id, task_sid],
    () =>
      Http.Request<ResponseFormat<IFormResponse[]>>(
        'GET',
        `/hotlines/api/v1/channel-forms.get?app_id=${app_id}&channel_id=${channel_id}&operator_sid=${operator_sid}&task_sid=${task_sid}&operator_profile_id=${operator_profile_id}&tab=${tab}`
      ).then((res) => {
        if (res.data.length === 1) FormsHelper.setSelectedResource(res.data[0].id, res.data[0].name);
        return res.data;
      }),
    {
      enabled: operator_profile_id !== '' && operator_profile_id !== undefined && app_id !== undefined && app_id !== '' && operator_sid !== undefined && task_sid !== undefined,
    }
  );
};

/**
 *!to search by keywords
  #########16th March 2022
 */

export const useSearchByKeyword = (
  channel_id: string,
  operator_profile_id: string,
  tab: string,
  search_content: string,
  app_id: string,
  operator_sid: string,
  task_sid: string,
  search_preference: string,
  start: number,
  size: number
): UseQueryResult<unknown> => {
  return useQuery(
    ['resources-keyword', channel_id, operator_profile_id, tab, search_content, app_id, operator_sid, task_sid, search_preference, start, size],
    () =>
      Http.Request<ResponseFormat<unknown>>(
        'GET',
        `/hotlines/api/v1/channel-resources.search?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&tab=${tab}&search_content=${search_content}&app_id=${app_id}&operator_sid=${operator_sid}&task_sid=${task_sid}&search_preference=${search_preference}&start=${start}&size=${size}`
      ).then((res) => res.data),
    {
      enabled: search_preference === 'keyword' && search_content !== '' && tab !== undefined,
    }
  );
};

/**
 *TO GET QUICK RESOURCES MARKED
 */

interface IQuickDocument {
  id: string;
  alias: string;
  display_name: string;
}

export function useResourcesMarkedAsQuick(channel_id: string, operator_profile_id: string, language: string): UseQueryResult<IQuickDocument[]> {
  return useQuery(
    ['channelwise-quick-resources', channel_id, operator_profile_id, language],
    () =>
      Http.Request<ResponseFormat<IQuickDocument[]>>(
        'GET',
        `/hotlines/api/v1/channel-quick-resources.get?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&language=${language}`
      ).then((res) => res.data),
    {
      enabled: channel_id !== undefined && operator_profile_id !== undefined,
      cacheTime: Number.POSITIVE_INFINITY,
      staleTime: Number.POSITIVE_INFINITY,
    }
  );
}

const fetchTaskScripts = async (channel_id: string, operator_profile_id: string, app_id: string, operator_sid: string, task_sid: string) =>
  Http.Request<ResponseFormat<IResourceNode[]>>(
    'GET',
    `/hotlines/api/v1/channel-script-tab.get?channel_id=${channel_id}&operator_profile_id=${operator_profile_id}&tab=Script&app_id=${app_id}&operator_sid=${operator_sid}&task_sid=${task_sid}`
  )
    .then((res) => res.data)
    .then((res) => {
      const allTabs = [];
      const tabContents = [];
      res.forEach((eachTab) => {
        allTabs.push({
          id: eachTab.id,
          displayName: eachTab.displayName,
          alias: eachTab.alias,
        });
        const allBuckets = [];
        if (eachTab.elements) Object.values(eachTab.elements).forEach((val) => allBuckets.push(val));
        tabContents.push({ id: eachTab.id, contents: allBuckets });
      });
      return {
        allTabs,
        tabContents,
      };
    });

export const useTaskScripts = (
  channel_id: string,
  operator_profile_id: string,
  app_id: string,
  operator_sid: string,
  task_sid: string,
  status = TaskRouterConfig.ASSIGNED
): UseQueryResult<{ allTabs: any[]; tabContents: any[] }> => {
  return useQuery(['task_script_resources', channel_id, operator_profile_id, task_sid], () => fetchTaskScripts(channel_id, operator_profile_id, app_id, operator_sid, task_sid), {
    enabled:
      operator_profile_id !== '' &&
      operator_profile_id !== undefined &&
      app_id !== undefined &&
      app_id !== '' &&
      operator_sid !== undefined &&
      task_sid !== undefined &&
      status === TaskRouterConfig.ASSIGNED,
  });
};

export const useTaskScriptDetails = (app_id: string, task_sid: string): UseMutationResult<unknown, unknown, { document_id: string; name: string }, unknown> => {
  return useMutation(async ({ document_id, name }) =>
    Http.Request<ResponseFormat<unknown>>('GET', `/hotlines/api/v1/omnichannel/tasks.script?id=${document_id}&name=${encodeURIComponent(name)}&app_id=${app_id}&task_sid=${task_sid}`).then(
      (res) => res.data
    )
  );
};